// SubmitButton — serializes the edited pose to .toon text and hands it to the
// platform commit handler (POST /pose/commit). The reference file-save path
// (loader.save → File System Access / download) is gone.

import * as React from 'react';
import { Button } from '../../components/ui/button';
import { t } from '../../config/strings';
import { buildAdjustedToon } from '../../io/serialize';
import { useStore } from '../../store';
import { allAudited, episodeElapsedMs } from '../../store/video';

export interface SubmitButtonProps {
  /** Commit handler supplied by the platform shell. */
  onSubmit: (toonText: string, frameCount: number, elapsedMs: number) => Promise<void>;
  submitting?: boolean;
  canSubmit?: boolean;
  blockedReason?: string | undefined;
}

export function SubmitButton({
  onSubmit,
  submitting = false,
  canSubmit = true,
  blockedReason,
}: SubmitButtonProps): React.ReactElement {
  const hasFolder = useStore((s) => s.folder !== null);
  const navigableFrames = useStore((s) => s.navigableFrames);
  const totalFrames = useStore((s) => s.totalFrames);
  const auditedFrames = useStore((s) => s.auditedFrames);
  const timerBaseMs = useStore((s) => s.timerBaseMs);
  const timerStartedAt = useStore((s) => s.timerStartedAt);
  const [error, setError] = React.useState<string | null>(null);

  const allSaved = allAudited(navigableFrames, totalFrames, auditedFrames);

  const handleClick = async (): Promise<void> => {
    if (!useStore.getState().folder) return;
    if (!canSubmit) {
      setError(blockedReason ?? 'A current Episode lease is required.');
      return;
    }
    setError(null);
    try {
      const store = useStore.getState();
      store.commitFrameSeeds(store.currentFrame);
      const { toonText, frameCount } = buildAdjustedToon();
      const elapsedMs = episodeElapsedMs({ timerBaseMs, timerStartedAt });
      await onSubmit(toonText, frameCount, elapsedMs);
    } catch (err) {
      console.error('pose submit failed:', err);
      setError(err instanceof Error ? err.message : 'Submit failed');
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <Button
        variant="default"
        className="w-full"
        disabled={!hasFolder || submitting || !allSaved || !canSubmit}
        title={!canSubmit ? blockedReason : undefined}
        onClick={handleClick}
        data-testid="submit-button"
      >
        {submitting ? t.submit.saving : t.submit.submit}
      </Button>
      {error !== null && (
        <span className="text-xs text-red-500" data-testid="submit-status">
          {t.submit.errorPrefix}
          {error}
        </span>
      )}
    </div>
  );
}
